import { FC, useEffect, useMemo } from "react";
import { round } from "lodash";
import moment from "moment";
import { Trans, useTranslation } from "react-i18next";
import { FaTriangleExclamation } from "react-icons/fa6";
import { IoIosLock } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";

import Loader from "@components/shared/Loader/Loader";
import PaymentCardBadge from "@components/User/PaymentCard/PaymentCardBadge";
import { BOOKING_CONSTANTS } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { usePaymentMethod } from "@hooks/usePaymentMethod/usePaymentMethod";
import { SavedPaymentMethodsProps } from "@models/components/paymentMethod.model";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";

export const SavedPaymentMethods: FC<SavedPaymentMethodsProps> = ({
  grandTotal = 0,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    cancellationDate,
    isLoading,
    savedMethods,
    scheduledTime,
    selectedMethodId,
    setSelectedMethodId,
  } = usePaymentMethod({ isBooking: true });

  const {
    bookingEditData,
    isEditBooking,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((state: RootState) => state.booking);

  const isEdit = Boolean(isEditBooking || bookingEditData?.bookingId);
  const tokenizationEnabled = Boolean(bookingEditData?.tokenizationEnabled);
  const tokenizationId = bookingEditData?.tokenizationId;

  const selectedResourceId = selectedSlot?.resourceId;
  const currentResourcePrice = selectedResourceId
    ? resourcePrices?.prices?.[selectedResourceId]
    : undefined;

  const isTokenizationAllowed =
    currentResourcePrice &&
    typeof currentResourcePrice.allowTokenization === "boolean"
      ? currentResourcePrice.allowTokenization
      : true;

  const currency = resourcePrices?.currency || CONSTANTS.CARD.DEFAULT_CURRENCY;
  const formattedAmount = grandTotal.toFixed(2);

  const amountToPaid = useMemo(() => {
    if (!isEditBooking || bookingEditData?.totalPrice == null) return 0;
    return round(grandTotal, 2) - round(bookingEditData.totalPrice, 2);
  }, [isEditBooking, bookingEditData?.totalPrice, grandTotal]);

  const displayAmount =
    isEditBooking && bookingEditData?.scheduledFrom === "ON_BOOKING"
      ? amountToPaid.toFixed(2)
      : formattedAmount;

  const shouldHideNotice =
    isEditBooking &&
    bookingEditData?.scheduledFrom === "ON_BOOKING" &&
    amountToPaid <= 0;

  const isInitialFreeBookingPayingNow =
    isEdit &&
    bookingEditData?.totalPrice !== undefined &&
    bookingEditData?.totalPrice !== null &&
    Number(bookingEditData.totalPrice) === 0 &&
    (amountToPaid > 0 || grandTotal > 0);

  const isNewPaymentMethodOnly =
    !isInitialFreeBookingPayingNow &&
    ((isEdit &&
      !tokenizationEnabled &&
      (tokenizationId === null || tokenizationId === undefined)) ||
      (isEdit &&
        !isTokenizationAllowed &&
        tokenizationId !== null &&
        tokenizationId !== undefined));

  const isSpecificTokenizedCardOnly =
    isEdit &&
    isTokenizationAllowed &&
    tokenizationEnabled &&
    tokenizationId !== null &&
    tokenizationId !== undefined;
  const rawCancellationDate = isEdit
    ? typeof bookingEditData?.cancellationUntil === "object"
      ? bookingEditData?.cancellationUntil?.date
      : bookingEditData?.cancellationUntil
    : cancellationDate;

  const cancellationMoment = useMemo(() => {
    if (!rawCancellationDate) return null;

    const parsed = moment(rawCancellationDate, [
      CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_SECONDS_FORMAT,
      CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_FORMAT,
      CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_HYPHEN_FORMAT,
      CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT,
    ]);

    if (parsed.isValid()) {
      return parsed;
    }

    const fallbackParsed = moment(rawCancellationDate);

    return fallbackParsed.isValid() ? fallbackParsed : null;
  }, [rawCancellationDate]);

  const isCancellationOver =
    !cancellationMoment ||
    cancellationMoment.clone().endOf("day").isBefore(moment());

  const formattedCancellationDate = !rawCancellationDate
    ? ""
    : cancellationMoment
      ? cancellationMoment.format(CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT)
      : String(rawCancellationDate);

  // Sync selected card ID (or null if "new") to Redux booking state
  useEffect(() => {
    const tokenizationId =
      selectedMethodId === CONSTANTS.CARD.NEW || !selectedMethodId
        ? null
        : selectedMethodId;
    dispatch(bookingSlice.actions.setSelectedTokenizationId(tokenizationId));
  }, [dispatch, selectedMethodId]);

  // Preselect method based on edit booking tokenization rules
  useEffect(() => {
    if (isNewPaymentMethodOnly) {
      if (selectedMethodId !== CONSTANTS.CARD.NEW) {
        setSelectedMethodId(CONSTANTS.CARD.NEW);
      }
    } else if (isSpecificTokenizedCardOnly) {
      const targetId = String(tokenizationId);
      if (selectedMethodId !== targetId) {
        setSelectedMethodId(targetId);
      }
    }
  }, [
    isNewPaymentMethodOnly,
    isSpecificTokenizedCardOnly,
    tokenizationId,
    selectedMethodId,
    setSelectedMethodId,
  ]);

  const { chargeDateStr, chargeHours, chargeTimeStr } = useMemo(() => {
    if (!scheduledTime) {
      return {
        chargeDateStr: "",
        chargeHours: 0,
        chargeTimeStr: "",
      };
    }

    try {
      const scheduleMoment = moment(scheduledTime, [
        CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_SECONDS_FORMAT,
        CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_FORMAT,
      ]);

      if (!scheduleMoment.isValid()) {
        return {
          chargeDateStr: "",
          chargeHours: 0,
          chargeTimeStr: "",
        };
      }

      let chargeHours = 0;
      if (selectedDate && selectedTime) {
        const slotDurationMinutes =
          Number(selectedSlot?.slot?.slot) || BOOKING_CONSTANTS.MINS_IN_HOUR;
        const bookingEndMoment = selectedSlot?.slot?.ends
          ? moment(
              `${selectedDate} ${selectedSlot.slot.ends}`,
              CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_FORMAT
            )
          : moment(
              `${selectedDate} ${selectedTime}`,
              CONSTANTS.DATE_FORMATS.YEAR_MONTH_DAY_TIME_FORMAT
            ).add(slotDurationMinutes, "minutes");

        if (bookingEndMoment.isValid()) {
          const rawDiff = Math.round(
            scheduleMoment.diff(bookingEndMoment, "hours", true)
          );
          if (rawDiff > 0) {
            chargeHours = rawDiff;
          }
        }
      }

      return {
        chargeDateStr: scheduleMoment.format(
          CONSTANTS.DATE_FORMATS.DAY_MONTH_YEAR_FORMAT
        ),
        chargeHours,
        chargeTimeStr: scheduleMoment.format(
          CONSTANTS.DATE_FORMATS.HOUR_MINUTE_FORMAT
        ),
      };
    } catch {
      return {
        chargeDateStr: "",
        chargeHours: 0,
        chargeTimeStr: "",
      };
    }
  }, [scheduledTime, selectedDate, selectedSlot, selectedTime]);

  const isScheduledTimeEmpty = !scheduledTime;

  const sortedSavedMethods = useMemo(
    () =>
      [...savedMethods].sort(
        (a, b) => Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault))
      ),
    [savedMethods]
  );

  const displayedSavedMethods = useMemo(() => {
    if (isNewPaymentMethodOnly) {
      return [];
    }
    if (isSpecificTokenizedCardOnly) {
      return sortedSavedMethods.filter(
        m => String(m.id) === String(tokenizationId)
      );
    }
    return sortedSavedMethods;
  }, [
    isNewPaymentMethodOnly,
    isSpecificTokenizedCardOnly,
    sortedSavedMethods,
    tokenizationId,
  ]);

  const showAddAnother = useMemo(() => {
    if (isNewPaymentMethodOnly) {
      return true;
    }
    if (isSpecificTokenizedCardOnly) {
      return false;
    }
    return true;
  }, [isNewPaymentMethodOnly, isSpecificTokenizedCardOnly]);

  const selectedMethod = useMemo(
    () =>
      sortedSavedMethods.find(m => String(m.id) === String(selectedMethodId)),
    [sortedSavedMethods, selectedMethodId]
  );

  const isSavedMethodMissingInEdit = useMemo(
    () =>
      isSpecificTokenizedCardOnly &&
      !sortedSavedMethods.some(m => String(m.id) === String(tokenizationId)),
    [isSpecificTokenizedCardOnly, sortedSavedMethods, tokenizationId]
  );

  useEffect(() => {
    dispatch(
      bookingSlice.actions.setSavedMethodMissingError(
        isSavedMethodMissingInEdit
      )
    );
    return () => {
      dispatch(bookingSlice.actions.setSavedMethodMissingError(false));
    };
  }, [dispatch, isSavedMethodMissingInEdit]);

  if (isLoading) {
    return <Loader isLoading={true} />;
  }

  if (isSavedMethodMissingInEdit) {
    return (
      <div
        className="alert alert-danger fg-saved-method-warning d-flex align-items-center gap-2 mt-3 small p-2 rounded"
        data-testid="saved-method-not-found-warning"
      >
        <FaTriangleExclamation className="flex-shrink-0" />
        <span>
          {t(
            "savedPaymentMethodNotFound",
            "You can’t edit this booking because admin has changed the finance settings. Please contact admin if you need to make any changes."
          )}
        </span>
      </div>
    );
  }

  if (isNewPaymentMethodOnly || !savedMethods?.length) {
    return (
      <div
        className="alert alert-light fg-tokenisation-info d-flex align-items-center gap-2 mt-3 p-3 rounded"
        data-testid="tokenisation-info"
      >
        <IoIosLock className="fg-tokenisation-info-icon flex-shrink-0" />
        <span>{t("tokenisationInfo")}</span>
      </div>
    );
  }

  const formatBrandTitle = (
    brand?: string,
    type?: string,
    lastFour?: string
  ) => {
    const b = (brand || "").trim().toUpperCase();
    if (type === CONSTANTS.CARD.TWINT_LOWER || b === CONSTANTS.CARD.TWINT) {
      return CONSTANTS.CARD.TWINT;
    }
    const formattedBrand = b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
    return lastFour ? `${formattedBrand} •••• ${lastFour}` : formattedBrand;
  };

  const selectedMethodTitle = selectedMethod
    ? selectedMethod.type === CONSTANTS.CARD.TWINT_LOWER ||
      selectedMethod.brand?.toUpperCase() === CONSTANTS.CARD.TWINT
      ? CONSTANTS.CARD.TWINT
      : selectedMethod.brand?.toUpperCase() || t("savedCard", "saved card")
    : t("savedPaymentMethod", "saved payment method");

  return (
    <div
      className="fg-saved-payment-methods mt-3"
      data-testid="saved-payment-methods"
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="fg-saved-methods-title m-0">
          {t("payWithSavedMethod")}
        </h6>
      </div>

      <div
        className="d-flex flex-column gap-2 mb-3"
        data-testid="saved-payment-methods-list"
      >
        {displayedSavedMethods.map(
          ({ brand, expMonth, expYear, id, isDefault, lastFour, type }) => {
            const isSelected = String(selectedMethodId) === String(id);

            const isValidExpiryValue = (value: unknown) =>
              Boolean(value) && value !== "undefined" && value !== "null";

            const hasExpiry =
              isValidExpiryValue(expMonth) && isValidExpiryValue(expYear);

            return (
              <div
                key={id}
                className={`fg-saved-method-card d-flex align-items-center ${isSelected ? "active" : ""}`}
                onClick={() => setSelectedMethodId(id)}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === "Enter" && setSelectedMethodId(id)}
                data-testid={`saved-method-${id}`}
              >
                <div className="fg-custom-radio d-flex align-items-center justify-content-center flex-shrink-0 me-3">
                  {isSelected && <div className="fg-radio-dot" />}
                </div>

                <PaymentCardBadge brand={brand} type={type} className="me-3" />

                <div className="d-flex flex-column">
                  <div className="d-flex align-items-center flex-wrap gap-2">
                    <span className="fg-method-title">
                      {formatBrandTitle(brand, type, lastFour)}
                    </span>
                    {isDefault && (
                      <span
                        className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary-subtle px-2 py-0.5 small fw-semibold"
                        data-testid="saved-method-default-badge"
                      >
                        {t("paymentMethods.default", "Default")}
                      </span>
                    )}
                  </div>
                  {hasExpiry && (
                    <div>
                      <span className="fg-method-subtitle mt-1 ">
                        {expMonth}/{expYear}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          }
        )}

        {showAddAnother && (
          <div
            className={`fg-saved-method-card d-flex align-items-center ${selectedMethodId === CONSTANTS.CARD.NEW ? "active" : ""}`}
            onClick={() => setSelectedMethodId(CONSTANTS.CARD.NEW)}
            role="button"
            tabIndex={0}
            onKeyDown={e =>
              e.key === "Enter" && setSelectedMethodId(CONSTANTS.CARD.NEW)
            }
            data-testid="saved-method-new"
          >
            <div className="fg-custom-radio d-flex align-items-center justify-content-center flex-shrink-0 me-3">
              {selectedMethodId === CONSTANTS.CARD.NEW && (
                <div className="fg-radio-dot" />
              )}
            </div>

            <div className="fg-plus-icon-circle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3">
              <i className="fa-solid fa-plus" />
            </div>

            <span className="fg-method-title">
              {t("addAnotherPaymentMethod")}
            </span>
          </div>
        )}
      </div>

      {!shouldHideNotice &&
        (selectedMethodId !== CONSTANTS.CARD.NEW ? (
          <div
            className="fg-auto-charge-notice d-flex align-items-start gap-2"
            data-testid="auto-charge-notice"
          >
            <i className="fa-regular fa-clock fg-notice-icon flex-shrink-0 mt-1" />
            <span className="fg-notice-text">
              <Trans
                i18nKey={
                  isScheduledTimeEmpty ||
                  bookingEditData?.scheduledFrom === "ON_BOOKING"
                    ? "autoChargeNoticeImmediate"
                    : isCancellationOver
                      ? "autoChargeNoticeNoCancellation"
                      : "autoChargeNotice"
                }
                values={{
                  amount: displayAmount,
                  cancellationDate: formattedCancellationDate,
                  currency,
                  date: chargeDateStr,
                  hours: chargeHours,
                  method: selectedMethodTitle,
                  time: chargeTimeStr,
                }}
                components={{
                  bold: <strong className="fw-bold" />,
                }}
              />
            </span>
          </div>
        ) : (
          <div
            className="fg-new-card-notice d-flex align-items-start gap-2"
            data-testid="new-card-notice"
          >
            <IoIosLock className="fg-notice-icon flex-shrink-0 mt-1" />
            <span className="fg-notice-text">
              <Trans
                i18nKey="newCardStepNotice"
                values={{
                  amount: displayAmount,
                  currency,
                }}
                components={{
                  bold: <strong className="fw-bold" />,
                }}
              />
            </span>
          </div>
        ))}
    </div>
  );
};

export default SavedPaymentMethods;
